// Server-sent events read from a fetch() body (Quick AI, Program tabs, Full
// Story). Frames are separated by a blank line; each has "event: <name>" and
// "data: <json>" lines, and lines starting with ":" are comments (heartbeats,
// the opening padding).
//
// Lines are split on "\n" only. The earlier per-frame regex treated U+2028 /
// U+2029 — which JSON.stringify leaves unescaped — as line ends, so a chunk
// containing one failed to parse and its text silently went missing.

/**
 * Take every complete frame out of `buffer`.
 * Returns { events: [{ event, data }], rest } — `rest` is the incomplete tail
 * to keep for the next read. Frames whose data isn't valid JSON are skipped.
 */
export function takeSSEFrames(buffer) {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const frames = normalized.split('\n\n');
  const rest = frames.pop();
  const events = [];
  for (const frame of frames) {
    let event = null;
    const dataLines = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith(':')) continue;
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(line[5] === ' ' ? 6 : 5));
    }
    if (!event || !dataLines.length) continue;
    try {
      events.push({ event, data: JSON.parse(dataLines.join('\n')) });
    } catch {
      // not JSON — not one of ours
    }
  }
  return { events, rest };
}

/**
 * Read a streamed response to the end, calling onEvent(event, data) for each
 * frame. Resolves with true if the stream ended by itself (the caller decides
 * whether a final "done" event arrived).
 */
export async function readSSE(body, onEvent) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = takeSSEFrames(buffer);
    buffer = rest;
    events.forEach(({ event, data }) => onEvent(event, data));
  }
  buffer += decoder.decode();
  // A last frame the server ended without its blank line.
  takeSSEFrames(`${buffer}\n\n`).events.forEach(({ event, data }) => onEvent(event, data));
  return true;
}
